'use client';

import React from 'react';
import { ArrowLeft, Clock, Calendar, X, Plus, ChevronRight, User } from 'lucide-react';
import { Service, Staff } from './types';

interface ServiceDetailViewProps {
  primaryService: Service;
  selectedServices: Service[];
  staffList: Staff[];
  selectedStaffId: string;
  onSelectStaff: (staffId: string) => void;
  onRemoveService: (serviceId: string) => void;
  onAddAnotherService: () => void;
  onProceedToSchedule: () => void;
  onBack: () => void;
}

export const ServiceDetailView: React.FC<ServiceDetailViewProps> = ({
  primaryService,
  selectedServices,
  staffList,
  selectedStaffId,
  onSelectStaff,
  onRemoveService,
  onAddAnotherService,
  onProceedToSchedule,
  onBack,
}) => {
  const totalDuration = selectedServices.reduce((acc, s) => acc + s.duration, 0);
  const totalCashPrice = selectedServices.reduce((acc, s) => acc + s.cashPrice, 0);

  // Determinar staff asignado visible
  const currentStaff = staffList.find((st) => st.id === selectedStaffId);
  const assignedStaffName = currentStaff ? currentStaff.name : 'Liliana';
  const staffInitial = assignedStaffName.charAt(0).toUpperCase();

  const primaryImage =
    primaryService.imageUrl ||
    'https://images.unsplash.com/photo-1599351431202-1e0f0137899a?w=900&h=650&fit=crop';

  return (
    <div className="w-full max-w-5xl mx-auto px-4 pb-24">
      {/* Barra de cabecera con Stepper de 3 pasos (AppGenda Fase 2) */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 pt-2">
        {/* Título y botón volver */}
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            aria-label="Volver"
            className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-700 hover:bg-slate-50 transition shadow-sm cursor-pointer shrink-0"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              NUEVA RESERVA
            </span>
            <div className="flex items-center gap-2">
              <h1 className="text-xl sm:text-2xl font-extrabold text-[#0f172a] tracking-tight">
                {primaryService.name}
              </h1>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 bg-white border border-slate-200 px-2.5 py-0.5 rounded-full shadow-2xs">
                <Clock className="w-3 h-3 text-slate-400" />
                {totalDuration} min
              </span>
            </div>
          </div>
        </div>

        {/* Stepper visual 1. Fecha y Hora | 2. Datos | 3. Confirmar */}
        <div className="flex items-center gap-1.5 self-center sm:self-auto text-xs font-bold">
          <span className="bg-[#1e293b] text-white px-3.5 py-1.5 rounded-full flex items-center gap-1.5 shadow-sm">
            <span className="w-4 h-4 rounded-full bg-white/20 text-white flex items-center justify-center text-[10px]">
              1
            </span>
            <span>Fecha y Hora</span>
          </span>
          <span className="w-4 h-px bg-slate-300" />
          <span className="bg-slate-200 text-slate-500 px-3 py-1.5 rounded-full flex items-center gap-1.5">
            <span className="w-4 h-4 rounded-full bg-slate-300 text-slate-600 flex items-center justify-center text-[10px]">
              2
            </span>
            <span>Datos</span>
          </span>
          <span className="w-4 h-px bg-slate-300" />
          <span className="bg-slate-200 text-slate-500 px-3 py-1.5 rounded-full flex items-center gap-1.5">
            <span className="w-4 h-4 rounded-full bg-slate-300 text-slate-600 flex items-center justify-center text-[10px]">
              3
            </span>
            <span>Confirmar</span>
          </span>
        </div>
      </div>

      {/* Grid en 2 Columnas (Estilo AppGenda Fases 1 y 2) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Columna Izquierda: Fotografía destacada y Tarjeta de Total */}
        <div className="lg:col-span-7 flex flex-col gap-4">
          <div className="w-full aspect-[4/3] rounded-3xl overflow-hidden shadow-md bg-slate-200 relative border border-slate-200/80">
            <img
              src={primaryImage}
              alt={primaryService.name}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Tarjeta de Total */}
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-500">Total</span>
            <span className="text-2xl sm:text-3xl font-extrabold text-[#0f172a] tracking-tight">
              ${' '}
              {totalCashPrice.toLocaleString('es-AR', {
                minimumFractionDigits: 0,
              })}
            </span>
          </div>
        </div>

        {/* Columna Derecha: Tarjeta Blanca Flotante con Carrito */}
        <div className="lg:col-span-5">
          <div className="bg-white rounded-3xl p-6 sm:p-7 shadow-xl border border-slate-100/90 flex flex-col gap-5">
            {/* Cabecera del bloque: Reservá tu turno */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
                <Calendar className="w-5 h-5 text-slate-700" />
              </div>
              <div>
                <h2 className="text-base font-bold text-[#0f172a]">
                  Reservá tu turno
                </h2>
                <p className="text-xs text-slate-500">
                  Elegí fecha y horario en el siguiente paso
                </p>
              </div>
            </div>

            <hr className="border-slate-100" />

            {/* Atiende: Profesional */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#c026d3] text-white flex items-center justify-center font-bold text-xs shadow-sm">
                  {staffInitial}
                </div>
                <div>
                  <span className="block text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                    ATIENDE
                  </span>
                  <span className="block text-xs font-bold text-[#0f172a]">
                    {assignedStaffName}
                  </span>
                </div>
              </div>

              {/* Selector de profesional si hay más de 1 */}
              {staffList.length > 1 && (
                <select
                  value={selectedStaffId}
                  onChange={(e) => onSelectStaff(e.target.value)}
                  className="text-xs font-semibold bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-slate-700 cursor-pointer focus:outline-none"
                >
                  <option value="any">Cualquiera disponible</option>
                  {staffList.map((st) => (
                    <option key={st.id} value={st.id}>
                      {st.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Bloque: TU CARRITO (N) */}
            <div className="flex flex-col gap-2.5">
              <div className="flex items-center gap-1.5 text-xs font-bold text-slate-700 uppercase tracking-wide">
                <span>TU CARRITO ({selectedServices.length})</span>
              </div>

              {/* Lista de servicios seleccionados */}
              <div className="flex flex-col gap-2">
                {selectedServices.map((srv, idx) => (
                  <div
                    key={srv.id}
                    className="bg-slate-100/80 rounded-xl p-3 flex items-center justify-between text-xs"
                  >
                    <div>
                      <p className="font-bold text-[#0f172a]">{srv.name}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        {srv.duration} min · {assignedStaffName}
                      </p>
                    </div>

                    {selectedServices.length > 1 && (
                      <button
                        onClick={() => onRemoveService(srv.id)}
                        className="text-slate-400 hover:text-rose-500 p-1 cursor-pointer transition"
                        title="Eliminar del carrito"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Botón Principal: Elegir fecha y horario */}
            <button
              onClick={onProceedToSchedule}
              className="w-full bg-[#1e293b] hover:bg-[#0f172a] text-white py-3.5 px-6 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 transition duration-200 shadow-lg shadow-slate-900/10 cursor-pointer active:scale-[0.99]"
            >
              <span>Elegir fecha y horario</span>
              <ChevronRight className="w-4 h-4" />
            </button>

            {/* Botón Secundario: + Sumar otro servicio (borde punteado como en AppGenda) */}
            <button
              onClick={onAddAnotherService}
              className="w-full bg-white hover:bg-slate-50 border-2 border-dashed border-slate-300 py-3 rounded-2xl font-semibold text-xs text-slate-700 flex items-center justify-center gap-1.5 transition cursor-pointer"
            >
              <Plus className="w-4 h-4 text-slate-500" />
              <span>Sumar otro servicio</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
